import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import NightDecor from './NightDecor';
import SnowDecor from './SnowDecor';

/**
 * Franja editorial de temporada: sello + copy ligado al negocio (no
 * decoración suelta). El ícono y el texto vienen de src/data/season.js —
 * cambiar de temporada es editar ese archivo, este componente y su layout
 * no se tocan.
 *
 * Cada `tone` es una combinación de colores ya medida contra su fondo:
 *  - paper:  fondo hueso elevado, acento kraft-deep (5.0:1).
 *  - night:  Halloween, tinta con acento calabaza (6.6:1; papel 14.9:1).
 *  - pino:   Navidad, abeto con acento oro (6.5:1; papel 11.1:1; salvia 8.8:1).
 */
const TONES = {
    paper: { bg: 'var(--paper-2)', eyebrow: 'var(--kraft-deep)', title: 'var(--ink)', text: 'var(--ink-soft)', link: 'var(--kraft-deep)', Decor: null },
    night: { bg: 'var(--ink)', eyebrow: 'var(--calabaza)', title: 'var(--paper)', text: 'var(--ink-dim)', link: 'var(--calabaza)', Decor: NightDecor },
    pino: { bg: 'var(--pino)', eyebrow: 'var(--oro-fiesta)', title: 'var(--paper)', text: 'var(--salvia)', link: 'var(--oro-fiesta)', Decor: SnowDecor },
};

/** Color de fondo de una franja, para que el rasgado de papel de arriba (TornEdge) coincida. */
export const toneBackground = (tone = 'paper') => (TONES[tone] || TONES.paper).bg;

export default function SeasonalBadge({ icon, eyebrow, title, text, tone = 'paper', cta = null }) {
    const t = TONES[tone] || TONES.paper;
    const Decor = t.Decor;
    return (
        <section
            /* Solo borde inferior en la franja clara: arriba llega el rasgado
               de TornEdge y una regla recta encima delataría el truco. */
            className={`reveal-on-scroll relative overflow-hidden ${Decor ? '' : 'border-b border-[var(--line)]'}`}
            style={{ backgroundColor: t.bg }}
            data-testid="seasonal-badge"
            data-tone={tone}
        >
            {Decor && <Decor />}
            <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-6 md:py-8 flex items-center gap-5 md:gap-7">
                <div className="flex-shrink-0">{icon}</div>
                <div className="min-w-0">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] mb-1" style={{ color: t.eyebrow }}>
                        {eyebrow}
                    </p>
                    <h2 className="font-heading text-base md:text-lg font-bold leading-snug text-balance" style={{ color: t.title }}>
                        {title}
                    </h2>
                    <p className="text-sm mt-1 leading-relaxed max-w-2xl" style={{ color: t.text }}>
                        {text}
                    </p>
                    {cta && (
                        <Link
                            to={cta.to}
                            data-testid="seasonal-cta"
                            className="inline-flex items-center gap-2 mt-3 font-mono text-[11px] uppercase tracking-[0.14em] underline-offset-4 hover:underline"
                            style={{ color: t.link }}
                        >
                            {cta.label} <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                    )}
                </div>
            </div>
        </section>
    );
}
